/**
 * Converts a numeric value to a compact, human-readable string using "K" for thousands and "M" for millions.
 * Negative numbers are wrapped in parentheses.
 *
 * Examples:
 * - 123       -> "123"
 * - 1500      -> "1.5K"
 * - 2000000   -> "2.0M"
 * - -1500     -> "(1.5K)"
 * - -2000000  -> "(2.0M)"
 * - -500      -> "(500)"
 *
 * @param number - The numeric value to format. Expected to be a finite number.
 * @param decimalPlaces - Optional. Number of decimal places to show (default: 1).
 * @returns A string representation using "K" for thousands and "M" for millions, with negative numbers in parentheses.
 *
 * @remarks
 * - Values < 1000 are returned as the original number's string form with specified decimal places.
 * - Values >= 1000 are divided and suffixed with "K" (thousands) or "M" (millions).
 * - Negative numbers are wrapped in parentheses while preserving the compact formatting.
 * - This implementation rounds to the specified decimal places (e.g., 1500 -> "1.5K" with decimalPlaces=1).
 */

const formatCompactNumber = (
  number: number,
  decimalPlaces: number = 1,
): string => {
  const isNegative = number < 0;
  const absNumber = Math.abs(number);

  let formatted: string;
  if (absNumber >= 1000000) {
    formatted =
      (absNumber / 1000000).toFixed(decimalPlaces).replace(/\.0+$/, "") + "M";
  } else if (absNumber >= 1000) {
    formatted =
      (absNumber / 1000).toFixed(decimalPlaces).replace(/\.0+$/, "") + "K";
  } else {
    formatted = absNumber.toFixed(decimalPlaces).replace(/\.0+$/, "");
  }

  return isNegative ? `(${formatted})` : formatted;
};

export default formatCompactNumber;
